import {
    formattedDollarsToCents,
    roundCentsToWholeDollarCents,
} from '@fruitful/fruitils';
import { getCategorizedRulesAndTotals } from '@fruitful/money-map';
import type {
    DeepPartial,
    MoneyMapAccountWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { MoneyMapRuleType, PaycheckRoutingRuleType } from '@fruitful/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToastController } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { captureException } from '../../logger/Logger';
import { centsToDollarInputDisplay } from './take-home-pay/moneyMapperIncomeForm';

export const useMoneyMapperGuideMonthlyBills = ({
    moneyMap,
    accounts,
    membershipBillsSumCents,
    onUpdateMoneyMap,
    isSaving = false,
}: {
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    accounts: MoneyMapAccountWithAccountDetails[];
    membershipBillsSumCents: number;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    /** When true (e.g. Money Map persist in flight), skip auto-syncing bills rule to bill sum. */
    isSaving?: boolean;
}) => {
    const toast = useToastController();

    const [isEditingGuideTotal, setIsEditingGuideTotal] = useState(false);
    const [guideDraftStr, setGuideDraftStr] = useState('');
    const [isSavingGuide, setIsSavingGuide] = useState(false);

    /** Last seen bills sum; used to detect changes and whether the rule was aligned with it. */
    const prevSourcesTotalCentsRef = useRef<number | null>(null);
    const billsRuleAutoSyncRef = useRef(false);
    /**
     * True after the user saves a **Monthly bills** headline that differs from the listed-bills
     * total. Cleared on "Remove manual" / successful auto-sync / saving an amount that matches bills.
     */
    const manualOverrideLockedRef = useRef(false);

    const { billsTotalCents: mapBillsRuleTotalCents } = useMemo(
        () => getCategorizedRulesAndTotals(moneyMap, accounts),
        [accounts, moneyMap],
    );

    const sourcesTotalCents = membershipBillsSumCents;

    const effectiveMonthlyBillsCents = useMemo(() => {
        if (mapBillsRuleTotalCents != null) {
            return mapBillsRuleTotalCents;
        }
        return sourcesTotalCents;
    }, [mapBillsRuleTotalCents, sourcesTotalCents]);

    const manualDiffersFromSourcesTotal =
        sourcesTotalCents > 0 &&
        mapBillsRuleTotalCents != null &&
        roundCentsToWholeDollarCents(mapBillsRuleTotalCents) !==
            roundCentsToWholeDollarCents(sourcesTotalCents);

    const showActualBillsSubline =
        sourcesTotalCents > 0 &&
        roundCentsToWholeDollarCents(effectiveMonthlyBillsCents) !==
            roundCentsToWholeDollarCents(sourcesTotalCents);

    useEffect(() => {
        if (!isEditingGuideTotal) {
            setGuideDraftStr(
                centsToDollarInputDisplay(effectiveMonthlyBillsCents),
            );
        }
    }, [effectiveMonthlyBillsCents, isEditingGuideTotal]);

    const updateBillsRuleAmount = useCallback(
        async (amountCents: number): Promise<boolean> => {
            const rules = [...(moneyMap.moneyMapRules ?? [])];
            const ruleIndex = rules.findIndex(
                (r) => r.type === MoneyMapRuleType.BILLS,
            );

            if (ruleIndex < 0 || rules[ruleIndex] == null) {
                toast.show(
                    'No bills rule on this Money Map yet. Add bills on the Bills tab first.',
                    MutationErrorToastOptions(),
                );
                return false;
            }

            rules[ruleIndex] = {
                ...rules[ruleIndex],
                // Monthly bills amount stored as a min-balance target. See PaycheckRoutingRuleType.
                routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
                targetBalanceCents: amountCents,
            };

            return await onUpdateMoneyMap({ moneyMapRules: rules });
        },
        [moneyMap.moneyMapRules, onUpdateMoneyMap, toast],
    );

    /**
     * When listed bills total changes, update the Money Map BILLS rule target **only** when the
     * user has not set a manual headline override (`manualOverrideLockedRef`) and the rule still
     * matched the previous listed-bills total (same idea as `!manualDiffersFromSourcesTotal` before
     * the change).
     */
    useEffect(() => {
        if (
            isEditingGuideTotal ||
            isSavingGuide ||
            isSaving ||
            billsRuleAutoSyncRef.current
        ) {
            return;
        }
        if (manualOverrideLockedRef.current) {
            prevSourcesTotalCentsRef.current = sourcesTotalCents;
            return;
        }
        if (mapBillsRuleTotalCents == null) {
            prevSourcesTotalCentsRef.current = sourcesTotalCents;
            return;
        }

        const prevSources = prevSourcesTotalCentsRef.current;
        if (prevSources === null) {
            prevSourcesTotalCentsRef.current = sourcesTotalCents;
            return;
        }
        if (sourcesTotalCents === prevSources) {
            return;
        }

        const ruleMatchedPreviousSources =
            roundCentsToWholeDollarCents(mapBillsRuleTotalCents) ===
            roundCentsToWholeDollarCents(prevSources);

        if (!ruleMatchedPreviousSources) {
            prevSourcesTotalCentsRef.current = sourcesTotalCents;
            return;
        }

        const nextCents = roundCentsToWholeDollarCents(sourcesTotalCents);
        if (
            roundCentsToWholeDollarCents(mapBillsRuleTotalCents) === nextCents
        ) {
            prevSourcesTotalCentsRef.current = sourcesTotalCents;
            return;
        }

        const sourcesAtSchedule = sourcesTotalCents;
        // Update eagerly so that if isSaving flips mid-flight (before refetch
        // completes) and the effect re-runs, it sees sourcesTotalCents ===
        // prevSources and bails out instead of queuing a redundant second save.
        prevSourcesTotalCentsRef.current = sourcesAtSchedule;
        let cancelled = false;
        billsRuleAutoSyncRef.current = true;
        void (async () => {
            try {
                const ok = await updateBillsRuleAmount(nextCents);
                if (!cancelled && ok) {
                    manualOverrideLockedRef.current = false;
                } else if (!cancelled && !ok) {
                    // Save failed — reset so the next bills change can retry.
                    prevSourcesTotalCentsRef.current = null;
                }
            } catch (error) {
                if (!cancelled) {
                    prevSourcesTotalCentsRef.current = null;
                }
                captureException(error);
            } finally {
                billsRuleAutoSyncRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
            billsRuleAutoSyncRef.current = false;
        };
    }, [
        isEditingGuideTotal,
        isSaving,
        isSavingGuide,
        mapBillsRuleTotalCents,
        moneyMap.moneyMapRules,
        onUpdateMoneyMap,
        sourcesTotalCents,
        updateBillsRuleAmount,
    ]);

    const removeManualGuideTotal = useCallback(async () => {
        setIsSavingGuide(true);
        try {
            const nextMonthly = roundCentsToWholeDollarCents(sourcesTotalCents);
            const ok = await updateBillsRuleAmount(nextMonthly);
            if (!ok) {
                return;
            }
            manualOverrideLockedRef.current = false;
            setIsEditingGuideTotal(false);
            toast.show(
                'Using total from listed bills',
                MutationSuccessToastOptions,
            );
        } catch (error) {
            captureException(error);
            toast.show('Error updating', MutationErrorToastOptions(error));
        } finally {
            setIsSavingGuide(false);
        }
    }, [sourcesTotalCents, toast, updateBillsRuleAmount]);

    const beginGuideInputEdit = useCallback(() => {
        setIsEditingGuideTotal(true);
        setGuideDraftStr(centsToDollarInputDisplay(effectiveMonthlyBillsCents));
    }, [effectiveMonthlyBillsCents]);

    const cancelGuideInputEdit = useCallback(() => {
        setIsEditingGuideTotal(false);
        setGuideDraftStr(centsToDollarInputDisplay(effectiveMonthlyBillsCents));
    }, [effectiveMonthlyBillsCents]);

    const commitGuideTotal = useCallback(async () => {
        const trimmed = guideDraftStr.trim();

        setIsSavingGuide(true);
        try {
            if (trimmed.length === 0) {
                toast.show(
                    'Enter a valid amount or cancel.',
                    MutationErrorToastOptions(),
                );
                return;
            }

            const parsed = roundCentsToWholeDollarCents(
                formattedDollarsToCents(guideDraftStr),
            );
            if (Number.isNaN(parsed) || parsed < 0) {
                toast.show(
                    'Enter a valid amount.',
                    MutationErrorToastOptions(),
                );
                return;
            }

            const ok = await updateBillsRuleAmount(parsed);
            if (!ok) {
                return;
            }
            const sourcesSnapshot =
                roundCentsToWholeDollarCents(sourcesTotalCents);
            manualOverrideLockedRef.current =
                roundCentsToWholeDollarCents(parsed) !== sourcesSnapshot;
            setIsEditingGuideTotal(false);
            toast.show('Monthly bills updated', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error);
            toast.show('Error saving', MutationErrorToastOptions(error));
        } finally {
            setIsSavingGuide(false);
        }
    }, [guideDraftStr, sourcesTotalCents, toast, updateBillsRuleAmount]);

    const canEditMoneyMapBills = useMemo(
        () =>
            (moneyMap.moneyMapRules ?? []).some(
                (r) => r?.type === MoneyMapRuleType.BILLS,
            ),
        [moneyMap.moneyMapRules],
    );

    return {
        effectiveMonthlyBillsCents,
        sourcesTotalCents,
        manualDiffersFromSourcesTotal,
        showActualBillsSubline,
        isSavingGuide,
        isEditingGuideTotal,
        guideDraftStr,
        setGuideDraftStr,
        commitGuideTotal,
        removeManualGuideTotal,
        beginGuideInputEdit,
        cancelGuideInputEdit,
        canEditMoneyMapBills,
    };
};

export type MoneyMapperGuideMonthlyBills = ReturnType<
    typeof useMoneyMapperGuideMonthlyBills
>;
